import React, { Fragment, useEffect, useRef, useState } from "react";
import { Tab } from "@headlessui/react";
import AuthButton from "../LogInPage/LoginButton";
import { getItem, setItem } from "../../utils/token";
import { useNavigate } from "react-router-dom";
import { setMinimisePanel, setResetFilters } from "../../redux/Slices/startFullTestSlice";
import { useDispatch } from "react-redux";
import { DashboardWidgets } from "../NewDashboard/DashboardCards";
import { AssessmentTabProps } from "../NewDashboard/Widgets/dataTypes";
import { MdOutlineInfo } from "react-icons/md";
import { openDialog } from "../../redux/Slices/dialogSlice";
import { checkCustomSATEnabled, formatDate } from "../../utils";
import FullMarksLoader from "../../components/ReUsableComponents/FullMarksLoader";

const ResultPageTabs = ({ resultsData, isLoading, studentId }: any) => {
  return (
    <div>
      <Tab.Group>
        <Tab.List className="relative flex">
          <Tab as={Fragment}>
            {({ selected }) => (
              <button
                className={`font-gully px-[10px] rounded-t text-base border border-b-0 focus:outline-none transition-all ${
                  selected
                    ? "text-appPrimaryColor py-[11px] border-appPrimaryColor"
                    : "bg-[#F5F9FF] py-2 text-[#646464] border-[#646464] mt-1"
                }`}
                role="tab"
              >
                Results
              </button>
            )}
          </Tab>
          <Tab as={Fragment}>
            {({ selected }) => (
              <button
                className={`font-gully ml-1 px-[10px]  rounded-t text-base border border-b-0 focus:outline-none transition-all ${
                  selected
                    ? "text-appPrimaryColor py-[11px] border-appPrimaryColor"
                    : "bg-[#F5F9FF] py-2 text-[#646464] border-[#646464] mt-1"
                }`}
                role="tab"
              >
                Assessment
              </button>
            )}
          </Tab>
        </Tab.List>

        <Tab.Panels >
          <Tab.Panel>
            <ResultTab resultData={resultsData} isLoading={isLoading} />
          </Tab.Panel>
          <Tab.Panel>
            <AssessmentTab studentId={studentId}/>
          </Tab.Panel>
        </Tab.Panels>
      </Tab.Group>
    </div>
  );
};

export default ResultPageTabs;

export const ResultTab = ({ resultData, isLoading }: any) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const handleViewResult = (userTestId: string, testName: string) => {
    if (userTestId && testName) {
      let currentTest = resultData.find((item: any) => item?.userTestId === userTestId);
      let updatedResults: any = currentTest?.additionalProps?.overallresult;
      setItem("overallResults", JSON.stringify(updatedResults));
      setItem("currentTestName", testName);
      navigate(`/practices/results/${userTestId}`);
      dispatch(setMinimisePanel(false));
      dispatch(setResetFilters());
    }
  };

  const getDifficultyLevel = (level: string) => {
    if(level === "EASY") {
      return "Easy";
    } else if(level === "MEDIUM") {
      return "Moderate";
    }
    return "Hard";
  }

  const userRole = getItem("userRole");
  const isCustomTestEnabled = checkCustomSATEnabled();
  return (
    <>
      {isLoading ? (
        <div className="flex justify-center items-center h-full rounded-[18px] border-[0.5px] border-satPrepGrey bg-satPrepTransWhite shadow-widget-card backdrop-blur-sevenFive">
          <FullMarksLoader width={100}  height={100} />
        </div>
      ) : (
        <div className="w-full py-3 rounded-[18px] max-h-[79vh] overflow-y-auto scroll4 border-[0.5px] border-satPrepGrey bg-satPrepTransWhite shadow-widget-card backdrop-blur-sevenFive">
          {/* Header */}
          <div className={`grid ${isCustomTestEnabled ? 'grid-cols-7' : 'grid-cols-4'} gap-4 pb-2 text-satPrepDarkAccent`}>
            <h3 className="p-[10px] text-center font-gully text-xl font-medium">
              Test Name
            </h3>
            <h3 className="p-[10px] text-center font-gully text-xl font-medium">
              Date
            </h3>
            {isCustomTestEnabled && (<>
              <h3 className="p-[10px] justify-center font-gully text-xl font-medium flex gap-2 ">
                <span>Type</span>
                <button onClick={() => dispatch(openDialog("testType"))} className="-mt-5 ml-2"><MdOutlineInfo size={15} /></button>
              </h3>
              <h3 className="p-[10px] text-center font-gully text-xl font-medium">
                Difficulty
                <p className="text-center text-satPrepXDarkAccent text-xs mt-1">R&W</p>
              </h3>
              <h3 className="p-[10px] text-center font-gully text-xl font-medium">
                Difficulty
                <p className="text-center text-satPrepXDarkAccent text-xs mt-1">MATH</p>
              </h3>
            </>)}
            <h3 className="p-[10px] text-center font-gully text-xl font-medium">
              Score
            </h3>
            <h3 className="p-[10px] text-center font-gully text-xl font-medium">
              Results
            </h3>
          </div>

          {/* Data Rows */}
          {resultData?.length > 0 ? (
            resultData.map((item: any) => (
              <div
                key={item.userTestId}
                className={`grid ${isCustomTestEnabled ? 'grid-cols-7' : 'grid-cols-4'} gap-3 items-center py-2 text-satPrepBlack`}
              >
                <h3 className="p-3 text-center font-gully text-base font-normal">
                  {item.name ?? "N/A"}
                </h3>
                <h3 className="text-nowrap text-center font-gully text-base font-normal">
                  {formatDate(item?.testTakenDate)}
                </h3>
                {isCustomTestEnabled && (
                  <>
                    <h3 className="p-3 text-center font-gully text-base font-normal">
                      {item?.isCustomTest ? "Custom" : "Standard"}
                    </h3>
                    <h3 className="p-3 text-center font-gully text-base font-normal capitalize">
                      {item?.isCustomTest
                        ? getDifficultyLevel(
                            item?.testDifficultyInfo?.readingAndWriting
                              ?.module1DifficultyLevel
                          )
                        : "-"}
                    </h3>
                    <h3 className="p-3 text-center font-gully text-base font-normal capitalize">
                      {item?.isCustomTest
                        ? getDifficultyLevel(
                            item?.testDifficultyInfo?.mathematics
                              ?.module1DifficultyLevel
                          )
                        : "-"}
                    </h3>
                  </>
                )}
                <h3 className="p-3 text-center font-gully text-base font-normal">
                  {item?.isCustomTest ? "-" : item?.finalScore?.totalScaledScore ?? "N/A"}
                </h3>
                <div className="block mx-auto">
                  {/* <button
                    onClick={() => handleViewResult(item?.userTestId, item?.name)}
                    className="flex w-32 py-2 px-3 justify-center items-center text-[16px] font-bold font-gully bg-appPrimaryColor rounded-md text-white hover:bg-white hover:text-appPrimaryColor border border-[#327AEC]"
                    style={{
                      boxShadow: "0px 4px 8px 0px rgba(28, 37, 44, 0.08)",
                    }}
                  >
                    View Result
                  </button> */}
                  <AuthButton
                    text="View"
                    onClick={() => handleViewResult(item?.userTestId, item?.name)}
                    className="font-gully text-sm w-32"
                  />
                </div>
              </div>
            ))
          ) : (
            <div className="w-full flex justify-center">
              {userRole === "PARENT" && (
                <div className="font-gully text-xl text-center">
                  No tests have been attempted yet
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export const AssessmentTab = ({studentId,testId}:AssessmentTabProps) => {
  
    const scrollRef = useRef<HTMLDivElement>(null);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const [showTopFade, setShowTopFade] = useState(false);
    const [showScroll, setShowScroll] = useState(false);

    const handleScroll = () => {
      const el = scrollRef.current;
      if (!el) return;

      setShowScroll(true);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        setShowScroll(false);
      }, 1000);
  
      const atTop = el.scrollTop === 0;
      setShowTopFade(!atTop);
    };
  
    useEffect(() => {
      const el = scrollRef.current;
      if (el) {
        el.addEventListener('scroll', handleScroll);
        // Initial check
        handleScroll();
      }
  
      return () => {
        el?.removeEventListener('scroll', handleScroll);
      };
    }, []);

  return (
    <div ref={scrollRef}  className={`h-full py-2 pl-3 w-full widgetScroll ${showScroll ? 'showScroll' : 'hideScroll'}`}>
      <DashboardWidgets studentId={studentId} testId={testId}/>
      {/* Top Gradient */}
      {showTopFade && (
        <div
          className="absolute  top-2 left-3 z-10 h-10 rounded-t-lg pointer-events-none"
          style={{
            width: 'calc(100vw - 266px)',
            background: 'linear-gradient(0deg, rgba(255, 255, 255, 0.00) 0%, rgba(255, 255, 255, 0.56) 50%, rgba(255, 255, 255, 0.75) 100%)',
          }}
        />
      )}
      {/* Bottom Gradient */}
      <div 
        className="absolute bottom-1 left-3 rounded-b-lg h-10 pointer-events-none"  
        style={{
          background: "linear-gradient(180deg, rgba(255, 255, 255, 0.00) 0%, rgba(255, 255, 255, 0.56) 50%, rgba(255, 255, 255, 0.75) 100%)",
          width: "calc(100vw - 266px)"
        }}
      />
    </div>
  );
};
